/**
 * Einzelnes Vorlagen-Formularelement
 */

import * as React from 'react';
import { IVorgangFormularElement, FahrzeugTyp, SES_TYP } from '../../models/ApiTypes';
import { Col, ControlLabel, FormControl, Row, FormGroup, Panel } from 'react-bootstrap';
import { bind, inject } from '../../utils/decorators';
import { observable, action } from 'mobx';
import { observer } from 'mobx-react';
import { AuftragStore } from '../../stores/AuftragStore';
import { ApiStore } from '../../stores/ApiStore';
import SepHellDunkelGrenze from './ses/SepHellDunkelGrenze';
import SepBeurteilungSaeule from './ses/SepBeurteilungSaeule';
import Agt4GasKalibrierung from './agt/Agt4GasKalibrierung';
import AgtOpazimeterKalibrierung from './agt/AgtOpazimeterKalibrierung';
import AgtPartikelzaehlerKalibrierung from './agt/AgtPartikelzaehlerKalibrierung';
import BpsReferenzkraftKalibrierung from './bps/BpsReferenzkraftKalibrierung';
import BpsReferenzkraftKalibrierungRili2021 from './bps/rili2021/BpsReferenzkraftKalibrierungRili2021';
import DmgKalibrierung from './dmg/DmgKalibrierung';
import FaIcon from '../site/FaIcon';
import FormularElementUrl from './formularelemente/FormularElementUrl';
import FormularElementFileUpload from './formularelemente/FormularElementFileUpload';
import FormularElementDocId from './formularelemente/FormularElementDocId';
import FormularElementTextUebernahme from './formularelemente/FormularElementTextUebernahme';
import FormularElementFotoUpload from './formularelemente/FormularElementFotoUpload';
import BpsReferenzkraftKalibrierungPlatteRili2021 from './bps/platte/BpsReferenzkraftKalibrierungPlatteRili2021';

export interface IVorgangFormularElementEingabe extends IVorgangFormularElement {
  visible: boolean;
  editable?: boolean;
  wert?: string;
  vorgangId?: string;
  onHandleChange?: (e: VorgangFormularElement) => void;
}

/** Rendert angepasstes nicht-standard Formularelemente aus einem Platzhalter, z.B. Messwerteingaben */
@observer
export default class VorgangFormularElement extends React.Component<IVorgangFormularElementEingabe, object> {

  @inject(AuftragStore) auftragStore: AuftragStore;
  @inject(ApiStore) apiStore: ApiStore;

  @observable validationstate: 'success' | 'warning' | 'error' | undefined;
  @observable value: string = this.props.wert ? this.props.wert : '';
  @observable checked: boolean = false;
  @observable visible: boolean = this.props.visible;

  constructor(props: IVorgangFormularElementEingabe) {
    super(props);
    // Standard Validierungsstatus für Felder mit Eingabemöglichkeit setzen
    if (this.requiresValidation()) {
      if (this.props.wert) {
        // Sind zu diesem Element Werte hinterlegt muss z.B. die Validierung manuell ausgelöst werden
        this.validationstate = this.props.wert === '' ? 'error' : undefined;
      } else {
        this.validationstate = 'error';
      }
    }
  }

  @bind @action handleValueChanged(event: React.FormEvent<FormControl>) {
    // TODO: Validierung nur bei optionalen Feldern
    this.validationstate = ((event.target as HTMLInputElement).value === '') ? 'error' : 'success';
    this.value = (event.target as HTMLInputElement).value;
    if (this.props.onHandleChange) {
      this.props.onHandleChange(this);
    }
  }

  @bind @action handleRadioChanged(valueIndex: number, value: string) {
    this.validationstate = (value === '') ? 'error' : undefined;
    this.value = value;
    this.checked = (value === this.props.werte[1]);
    if (this.props.onHandleChange) {
      this.props.onHandleChange(this);
    }
  }

  @bind @action handleCustomValidation(validationstate: 'success' | 'warning' | 'error' | undefined) {
    this.validationstate = validationstate;
    // Änderung nach oben propagieren
    if (this.props.onHandleChange) {
      this.props.onHandleChange(this);
    }
  }

  /**
   * Gibt an ob dieses Element validiert werden muss, abhängig vom Typ des Elements und davon ob es optional ist
   *
   * @returns boolean Gibt true zurück wenn das Element validiert werden muss, ansonsten false
   */
  public requiresValidation(): boolean {
    return ((['checkbox', 'select', 'text', 'textarea', 'date', 'number', 'number_int', 'fileupload', 'fotoupload', 'docid', 'text_uebernahme'].indexOf(this.props.typ) >= 0) && (this.props.optional === false));
  }

  private getValidationState() {
    return (this.props.editable) ? this.validationstate : undefined;
  }

  // Gibt anhand der in der Kategorie hinterlegten Vorlage die passende React-Komponente zurück
  public renderVorlage() {
    switch (this.props.vorlage) {

      // Tabelle für Eingabe der Messwerte zur Beurteilung der Hell/Dunkel-Grenze des SEPs
      case 'sep_messwerte_helldunkelgrenze': {
        let fahrzeugTyp: FahrzeugTyp = 'PKW';
        if (this.auftragStore.auftrag) {
          const fahrzeugTypMeta = this.auftragStore.auftrag.meta.find(meta => meta.bezeichnung === SES_TYP);
          if (fahrzeugTypMeta) {
            fahrzeugTyp = fahrzeugTypMeta.inhalt as FahrzeugTyp;
          }
        }
        return(
          <SepHellDunkelGrenze
            typ={fahrzeugTyp}
            kategorieguid={this.props.kategorieguid}
            elementguid={this.props.elementguid}
            editable={this.props.editable}
            value={this.value}
            element={this}
            onHandleChange={this.props.onHandleChange}/>
        );
      }

      // Tabelle für Eingabe der Messwerte zur Beurteilung der Gradheit der Säule des SEPs
      case 'sep_messwerte_beurteilungsäule': {
        return(
          <SepBeurteilungSaeule
            kategorieguid={this.props.kategorieguid}
            elementguid={this.props.elementguid}
            editable={this.props.editable}
            value={this.value}
            element={this}
            onHandleChange={this.props.onHandleChange}
          />
        );
      }

      // Messwerteingabe 4-Gas Abgasmessgerät
      case 'agt_4gas_kalibrierung': {
        return(
          <Agt4GasKalibrierung
            kategorieguid={this.props.kategorieguid}
            elementguid={this.props.elementguid}
            editable={this.props.editable}
            value={this.value}
            element={this}
          />
        );
      }

      // Messwerteingabe Opazimeter Abgasmessgerät
      case 'agt_opazimeter_kalibrierung': {
        return(
          <AgtOpazimeterKalibrierung
            kategorieguid={this.props.kategorieguid}
            elementguid={this.props.elementguid}
            editable={this.props.editable}
            value={this.value}
            element={this}
          />
        );
      }

      // Messwerteingabe Abgasmessgerät Partikelzähler
      case 'agt_partikel_dc_kalibrierung': {
        return(
          <AgtPartikelzaehlerKalibrierung
            kategorieguid={this.props.kategorieguid}
            elementguid={this.props.elementguid}
            editable={this.props.editable}
            value={this.value}
            element={this}
          />
        );
      }

      // Kalibrierung Bremsenprüfstand mit Referenzkraft-Verfahren
      case 'bps_referenzkraft_kalibrierung': {
        return(
          <BpsReferenzkraftKalibrierung
            kategorieguid={this.props.kategorieguid}
            elementguid={this.props.elementguid}
            editable={this.props.editable}
            value={this.value}
            element={this}
          />
        );
      }

      // Messwerteingabe Druckmessgerät
      case 'dmg_handpumpe_kalibrierschein': {
        return(
          <DmgKalibrierung
            kategorieguid={this.props.kategorieguid}
            elementguid={this.props.elementguid}
            editable={this.props.editable}
            value={this.value}
            element={this}
          />
        );
      }
      case 'bps_referenzkraft_kalibrierung_rili_2021': {
        return(
          <BpsReferenzkraftKalibrierungRili2021
            kategorieguid={this.props.kategorieguid}
            elementguid={this.props.elementguid}
            editable={this.props.editable}
            value={this.value}
            element={this}
          />
        );
      }
      case 'bps_referenzkraft_kalibrierung_platte_rili_2021': {
        return(<BpsReferenzkraftKalibrierungPlatteRili2021 {...this.props} value={this.value} />);
      }
      default:
        return(<div className="maxwidth" style={{color: 'red'}}>{`Unbekannte Vorlage "${this.props.vorlage}"`}</div>);
    }
  }

  public render() {
    const { kategorieguid, elementguid, typ, text, werte, editable, wert, platzhalter } = this.props;
    let rowClass = 'formular-element-row';
    if (this.props.abhaengigvon !== null) {
      rowClass = 'formular-element-abhaengig';
    }
    switch (typ) {
      case 'checkbox':
        return (
          <Row className={rowClass}>
            <FormGroup controlId={text} validationState={this.getValidationState()} className="formular-element-row">
              <Col componentClass={ControlLabel} sm={7} className="formular-element-caption">{text}</Col>
              <Col componentClass={ControlLabel} sm={5} className="formular-element">
                {editable ?
                  <div className="pull-right">
                    <div className="radio radio-success radio-inline">
                        <input
                          type="radio"
                          name={`${kategorieguid}[${elementguid}]`}
                          id={`1_${kategorieguid}[${elementguid}]`}
                          checked={this.value === werte[1]}
                          onChange={e => { this.handleRadioChanged(1, werte[1]); }}
                          value={werte[1]}/>
                        <label htmlFor={`1_${kategorieguid}[${elementguid}]`}>
                          {werte[1]}
                        </label>
                    </div>
                    <div className="radio radio radio-inline">
                        <input
                          type="radio"
                          name={`${kategorieguid}[${elementguid}]`}
                          id={`0_${kategorieguid}[${elementguid}]`}
                          checked={this.value === werte[0]}
                          onChange={e => { this.handleRadioChanged(0, werte[0]); }}
                          value={werte[0]}/>
                        <label htmlFor={`0_${kategorieguid}[${elementguid}]`}>
                          {werte[0]}
                        </label>
                    </div>
                  </div>
                  :
                  <p className="pull-right">{wert}</p>
                }
              </Col>
            </FormGroup>
          </Row>
        );
      case 'select':
        return (
          <Row className={rowClass}>
            <FormGroup controlId={text} validationState={this.getValidationState()} className="formular-element-row">
              <Col componentClass={ControlLabel} sm={7} className="formular-element-caption">{text}</Col>
              <Col componentClass={ControlLabel} sm={5} className="formular-element">
                {editable ?
                  <FormControl readOnly={!editable} componentClass="select" placeholder="select" onChange={this.handleValueChanged} type="text" name={`${kategorieguid}[${elementguid}]`} value={this.value}>
                    <option value={undefined}/>
                    {
                      werte.map(function(val: string) {
                        return <option key={val} value={val}>{val}</option>;
                      })
                    }
                  </FormControl>
                  :
                  <p>{wert}</p>
                }
              </Col>
            </FormGroup>
          </Row>
        );
      case 'text':
        return (
          <Row className={rowClass}>
            <FormGroup controlId={text} validationState={this.getValidationState()} className="formular-element-row">
              <Col componentClass={ControlLabel} sm={9} className="formular-element-caption">{text}</Col>
              <Col componentClass={ControlLabel} sm={3} className="formular-element">
                <FormControl readOnly={!editable} onChange={this.handleValueChanged} type="text" name={`${kategorieguid}[${elementguid}]`} value={this.value} placeholder={platzhalter}/>
              </Col>
            </FormGroup>
          </Row>
        );
      case 'textarea':
        return (
          <FormGroup controlId={text} validationState={this.getValidationState()} className="formular-element-row">
            <Row className={rowClass}>
              <Col componentClass={ControlLabel} sm={9} className="formular-element-caption">{text}</Col>
            </Row>
            <Row className={rowClass}>
              <Col componentClass={ControlLabel} sm={12} className="formular-element">
                <FormControl readOnly={!editable} onChange={this.handleValueChanged} type="textarea" name={`${kategorieguid}[${elementguid}]`} value={this.value} placeholder={platzhalter}/>
              </Col>
            </Row>
          </FormGroup>
        );
      case 'textblock':
        return (
          <FormGroup controlId={text} validationState={this.getValidationState()} className="formular-element-row">
            {text.trim() !== '' &&
              <Row className={rowClass}>
                <Col componentClass={ControlLabel} sm={9} className="formular-element-caption">{text}</Col>
              </Row>
            }
            <Row className={rowClass}>
              <Col componentClass={ControlLabel} sm={12} className="formular-element">
                <FormControl readOnly={!editable} onChange={this.handleValueChanged} componentClass="textarea" type="text" name={`${kategorieguid}[${elementguid}]`} value={this.value} placeholder={platzhalter} rows={5} style={{height: 'auto !important'}}/>
              </Col>
            </Row>
          </FormGroup>
        );
      case 'date':
        return (
          <Row className={rowClass}>
            <FormGroup controlId={text} validationState={this.getValidationState()} className="formular-element-row">
              <Col componentClass={ControlLabel} sm={9} className="formular-element-caption">{text}</Col>
              <Col componentClass={ControlLabel} sm={3} className="formular-element">
                <FormControl readOnly={!editable} onChange={this.handleValueChanged} type="date" name={`${kategorieguid}[${elementguid}]`} value={this.value}/>
              </Col>
            </FormGroup>
          </Row>
        );
      case 'number':
        return (
          <Row className={rowClass}>
            <FormGroup controlId={text} validationState={this.getValidationState()} className="formular-element-row">
              <Col componentClass={ControlLabel} sm={9} className="formular-element-caption">{text}</Col>
              <Col componentClass={ControlLabel} sm={3} className="formular-element">
                <FormControl readOnly={!editable} step={0.01} onChange={this.handleValueChanged} type="number" name={`${kategorieguid}[${elementguid}]`} value={this.value} placeholder={platzhalter}/>
              </Col>
            </FormGroup>
          </Row>
        );
      case 'number_int':
        return (
          <Row className={rowClass}>
            <FormGroup controlId={text} validationState={this.getValidationState()} className="formular-element-row">
              <Col componentClass={ControlLabel} sm={9} className="formular-element-caption">{text}</Col>
              <Col componentClass={ControlLabel} sm={3} className="formular-element">
                <FormControl readOnly={!editable} step={1} onChange={this.handleValueChanged} type="number" name={`${kategorieguid}[${elementguid}]`} value={this.value} placeholder={platzhalter}/>
              </Col>
            </FormGroup>
          </Row>
        );
      case 'helptext':
        return (
          <Row className={rowClass}>
            <Col sm={12}><Panel className="formular-element-helptext"><table style={{border: '0px'}}><tr><td><FaIcon icon="lightbulb-o" style={{fontSize: '22px', paddingRight: '10px'}}/></td><td>{text}</td></tr></table></Panel></Col>
          </Row>
        );
      case 'image':
        return (
          <Row className={rowClass}>
            <img src={this.apiStore.vorlagenElementImageUrl(elementguid)} title={text} alt={text} className="formular-element-image"/>
          </Row>
        );
      case 'url':
        return(
          <Row className={rowClass}>
            <Col sm={12}>
              <FormularElementUrl
                text={text}
                url={werte[0]}
                icon={werte.length > 1 ? werte[1] : undefined}
                target="_blank"
                tokenErfoderlich={true}/>
            </Col>
          </Row>
        );
      case 'fileupload':
        return (
          <Row className={rowClass}>
            <Col sm={12}>
              <FormularElementFileUpload
                vorgangId={this.props.vorgangId}
                text={text}
                dokumentenBezeichnung={werte[0]}
                dokumentTitel={werte[0]}
                element={this}
                name={`${kategorieguid}[${elementguid}]`}
                wert={wert}
                onHandleValidation={this.handleCustomValidation}/>
            </Col>
          </Row>
        );
      case 'fotoupload':
        return (
          <Row className={rowClass}>
            <Col sm={12}>
                {this.props.vorgangId &&
                  <FormularElementFotoUpload
                    vorgangId={this.props.vorgangId}
                    kategorie={werte[0]}
                    name={`${kategorieguid}[${elementguid}]`}
                    onHandleValidation={this.handleCustomValidation}/>
                }
            </Col>
          </Row>
        );
      case 'docid':
        return(
          <Row className={rowClass}>
            <FormularElementDocId
              editable={editable}
              kategorieguid={kategorieguid}
              elementguid={elementguid}
              value={wert}
              platzhalter={platzhalter}
              text={text}
              werte={werte[0]}
              anr={this.auftragStore.auftrag ? this.auftragStore.auftrag.anr : undefined}
              onHandleValidation={this.handleCustomValidation}/>
          </Row>
        );
        case 'text_uebernahme':
          return(
            <Row className={rowClass}>
              <FormularElementTextUebernahme
                editable={editable}
                kategorieguid={kategorieguid}
                elementguid={elementguid}
                value={wert}
                platzhalter={platzhalter}
                text={text}
                werte={werte[0]}
                anr={this.auftragStore.auftrag ? this.auftragStore.auftrag.anr : undefined}
                onHandleValidation={this.handleCustomValidation}/>
            </Row>
          );
      case 'vorlage':
        return (
          <Row className="formular-element-vorlage">
            {this.renderVorlage()}
          </Row>
        );
      default:
        return (
          <Row className={rowClass}>
            <Col componentClass={ControlLabel} sm={12} className="formular-element-caption">{text}</Col>
            <Col componentClass={ControlLabel} sm={0}/>
          </Row>
        );
    }
  }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorlagen/VorgangFormularElement.tsx